import { DeviceDisplayInfo, TmapApp } from '@tmap-web-lib/tmap-app-interface'
import { getRandomNumber } from '@tmap-web-lib/utils'

import { STORAGE_ACCESS_KEY } from '@/atoms'

export function applyTmapAppBrowserProxy() {
  /**
   * 브라우저 환경 프록시 설정.
   * 아무 동작 안하지만 콘솔 로그를 출력하기 위해 호출.
   */
  const {
    getAccessKey,
    getCurrentPosition,
    getUserSetting,
    openServiceByUrl,
    openBrowser,
    getEUK,
    updateAccessKey,
    getRemoteConfig,
    getDisplayInfo,
    getSessionId,
    shareMessage,
    getUserAllSettings,
    getFavoriteGroup,
    openFavoriteGroupPopup,
    getFavoriteState,
    getDeviceId,
    syncTerms,
    openAgreementDialog,
    checkPedometerPermission,
    requestPedometerPermission,
    updatePedometerSteps,
  } = TmapApp

  // 새로고침 전까지 유지.
  const sessionId = (getRandomNumber() + 1) % 100_000_000

  Object.assign(TmapApp, {
    getAccessKey: async () => {
      getAccessKey.call(TmapApp)
      return getItem(STORAGE_ACCESS_KEY)
    },
    getCurrentPosition: () => {
      getCurrentPosition.call(TmapApp)
      return new Promise((resolve) => {
        window.navigator.geolocation.getCurrentPosition(
          (position) => {
            resolve([position.coords.latitude, position.coords.longitude])
          },
          () => {
            resolve([37.5667, 126.9785])
          },
          {
            timeout: 5000,
          }
        )
      })
    },
    getUserSetting: async (args: Parameters<typeof getUserSetting>[0]) => {
      getUserSetting.call(TmapApp, args)
      return getItem(`TmapApp.getUserSetting.${args.key}`)
    },
    openServiceByUrl: (args: Parameters<typeof openServiceByUrl>[0]) => {
      openServiceByUrl.call(TmapApp, args)
      const otherWindow = window.open(args.url, '_blank')
      return new Promise<void>((resolve) => {
        const check = () => {
          if (otherWindow == null || otherWindow.closed) {
            resolve()
            return
          }
          setTimeout(check, 1000)
        }
        check()
      })
    },
    openBrowser: (args: Parameters<typeof openBrowser>[0]) => {
      openBrowser.call(TmapApp, args)
      window.open(args.url)
    },
    getEUK: async () => {
      getEUK.call(TmapApp)
      return getItem('TmapApp.getEUK') || ''
    },
    updateAccessKey: (args: Parameters<typeof updateAccessKey>[0]) => {
      updateAccessKey.call(TmapApp, args)
      setItem(STORAGE_ACCESS_KEY, args.key)
    },
    getRemoteConfig: async (args: Parameters<typeof getRemoteConfig>[0]) => {
      getRemoteConfig.call(TmapApp, args)
      return getItem(`TmapApp.getRemoteConfig.${args.key}`) || ''
    },
    getDisplayInfo: async () => {
      getDisplayInfo.call(TmapApp)
      return {
        displayHeight: window.innerHeight,
        displayWidth: window.innerWidth,
        screenHeight: window.innerHeight,
        screenWidth: window.innerWidth,
        webviewHeight: window.innerHeight,
        webviewWidth: window.innerWidth,
        statusBarHeight: Number(getItem('TmapApp.getDisplayInfo.statusBarHeight')) || 0,
        navigationBarHeight: 0,
        orientation: 1,
      } satisfies DeviceDisplayInfo
    },
    getSessionId: async () => {
      getSessionId.call(TmapApp)
      return sessionId
    },
    shareMessage: async (args: Parameters<typeof shareMessage>[0]) => {
      shareMessage.call(TmapApp, args)
      window.open(args.body?.match(/(https:\/\/.+?)\n/)?.[0] || args.url)
    },
    getUserAllSettings: async () => {
      getUserAllSettings.call(TmapApp)
      const stored = getItem('TmapApp.getUserAllSettings')
      if (stored) return stored
      return {
        settings: {
          user: {
            openProfileId: 'udy8em8tz2',
          },
          car: {},
        },
      }
    },
    getFavoriteGroup: async (args: Parameters<typeof getFavoriteGroup>[0]) => {
      getFavoriteGroup.call(TmapApp, args)
      return {
        poiDataList: args.poiIdList.filter(() => getRandomNumber() % 4 === 0).map((poiId) => ({ poiId })),
        poiGroupList: [],
      }
    },
    openFavoriteGroupPopup: async (args: Parameters<typeof openFavoriteGroupPopup>[0]) => {
      openFavoriteGroupPopup.call(TmapApp, args)
      return {
        result: getRandomNumber() % 2 === 0,
      }
    },
    getFavoriteState: async (args: Parameters<typeof getFavoriteState>[0]) => {
      getFavoriteState.call(TmapApp, args)
      return {
        result: getRandomNumber() % 2 === 0,
      }
    },
    getDeviceId: async () => {
      getDeviceId.call(TmapApp)
      return getItem('TmapApp.getDeviceId') || 'WEB_DEVICE'
    },
    syncTerms: async (args: Parameters<typeof syncTerms>[0]) => {
      syncTerms.call(TmapApp, args)
      return { result: true }
    },
    openAgreementDialog: async (args: Parameters<typeof openAgreementDialog>[0]) => {
      openAgreementDialog.call(TmapApp, args)
      return { result: true }
    },
    checkPedometerPermission: async () => {
      checkPedometerPermission.call(TmapApp)
      return { result: true }
    },
    requestPedometerPermission: async () => {
      requestPedometerPermission.call(TmapApp)
      return { result: true }
    },
    updatePedometerSteps: async (args: Parameters<typeof updatePedometerSteps>[0]) => {
      updatePedometerSteps.call(TmapApp, args)
      return { result: true }
    },
  })
}

function getItem(key: string) {
  const item = window.localStorage.getItem(key)
  if (item) {
    try {
      return JSON.parse(item)
    } catch {
      return item
    }
  }
  return null
}

function setItem(key: string, value: string) {
  window.localStorage.setItem(key, JSON.stringify(value))
}
